"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.socialSecurityNumberSchema = exports.SOCIAL_SECURITY_NUMBER_INVALID_MESSAGE = exports.formatSocialSecurityNumber = exports.SOCIAL_SECURITY_NUMBER_SEPARATOR = exports.isValidSocialSecurityNumber = exports.normalizeSocialSecurityNumber = void 0;
const zod_1 = require("zod");
/** Length of a NIR once normalized: 13 body characters + a 2-digit control key. */
const SOCIAL_SECURITY_NUMBER_LENGTH = 15;
/**
 * Structural shape of a normalized NIR: sex, birth year, birth month (`01-12`
 * plus the `20-99` codes used when the month is unknown), birth department
 * (numeric or Corsica), then commune, order number and control key.
 */
const SOCIAL_SECURITY_NUMBER_PATTERN = /^[1-8]\d{2}(?:0[1-9]|1[0-2]|[2-9]\d)(?:\d{2}|2[AB])\d{8}$/;
/**
 * Strips the separators people type (spaces, dots, dashes) and uppercases the
 * Corsica department letter, yielding the canonical 15-character form stored in
 * database.
 *
 * @param value - the raw user input
 * @returns the normalized NIR
 */
const normalizeSocialSecurityNumber = (value) => value.replace(/[\s.-]/g, '').toUpperCase();
exports.normalizeSocialSecurityNumber = normalizeSocialSecurityNumber;
/**
 * Checks the structure of a French social security number (NIR): 15 characters
 * laid out as sex, birth year, birth month, birth department, commune, order
 * number and control key.
 *
 * The control key is deliberately NOT verified. Every NIR that INSEE issues
 * satisfies `key = 97 - (body mod 97)`, so checking it would cost nothing on
 * real cards — but it also rejects the employer-assigned provisional numbers
 * that show up in payroll, and blocks placeholder values during testing. The
 * team chose to accept a well-formed number and leave the key alone.
 *
 * The input may carry spaces, dots or dashes — it is normalized first.
 *
 * @param value - the raw or normalized NIR
 * @returns `true` when the number is structurally a NIR
 * @example
 * isValidSocialSecurityNumber('2 69 05 49 588 157 80'); // true
 */
const isValidSocialSecurityNumber = (value) => SOCIAL_SECURITY_NUMBER_PATTERN.test((0, exports.normalizeSocialSecurityNumber)(value));
exports.isValidSocialSecurityNumber = isValidSocialSecurityNumber;
/**
 * Sizes of the NIR display groups: sex, birth year, birth month, birth
 * department, commune, order number, control key.
 */
const SOCIAL_SECURITY_NUMBER_GROUP_SIZES = [1, 2, 2, 2, 3, 3, 2];
/** Separator between two NIR display groups. */
exports.SOCIAL_SECURITY_NUMBER_SEPARATOR = ' ';
/**
 * Groups a NIR for display — `269054958815780` reads `2 69 05 49 588 157 80`.
 *
 * Accepts partial input, so it can format a number as it is being typed, and
 * normalizes first, so an already-grouped value is regrouped rather than
 * doubled up. Extra characters beyond the 15th are dropped.
 *
 * @param value - the raw, partial or normalized NIR
 * @returns the grouped NIR, or an empty string when there is nothing to show
 * @example
 * formatSocialSecurityNumber('269054958815780'); // '2 69 05 49 588 157 80'
 */
const formatSocialSecurityNumber = (value) => {
    const normalized = (0, exports.normalizeSocialSecurityNumber)(value).slice(0, SOCIAL_SECURITY_NUMBER_LENGTH);
    const groups = [];
    let offset = 0;
    for (const size of SOCIAL_SECURITY_NUMBER_GROUP_SIZES) {
        if (offset >= normalized.length) {
            break;
        }
        groups.push(normalized.slice(offset, offset + size));
        offset += size;
    }
    return groups.join(exports.SOCIAL_SECURITY_NUMBER_SEPARATOR);
};
exports.formatSocialSecurityNumber = formatSocialSecurityNumber;
/** i18n key surfaced by forms when the NIR is not structurally valid. */
exports.SOCIAL_SECURITY_NUMBER_INVALID_MESSAGE = 'form.errors.invalid-social-security-number';
/**
 * Zod schema for a normalized French social security number. Used by the API
 * payload contracts and by the web forms, so both reject the same values.
 */
exports.socialSecurityNumberSchema = zod_1.z
    .string()
    .length(SOCIAL_SECURITY_NUMBER_LENGTH, {
    message: exports.SOCIAL_SECURITY_NUMBER_INVALID_MESSAGE,
})
    .refine(exports.isValidSocialSecurityNumber, {
    message: exports.SOCIAL_SECURITY_NUMBER_INVALID_MESSAGE,
});
